import { Grid, Text, Accordion, useDate, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent } from 'react'

import { Money } from '../'
import styles from './style.module.css'
import { CheckoutPaymentSchedule } from '@afterpaytouch/portal-api'
import { useDonation, useIsCheckoutLoadingOrUpdating, useCalculatedPaymentSchedules, useSelectedPaymentOption, useAppRouter } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'

export const ConsumerLendingPaymentSchedule: FunctionComponent = () => {
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const formatDateLocale = locale === 'en-US' ? 'en-AU' : locale
  const { formatDate } = useDate({ locale: formatDateLocale, weekday: 'short', year: '2-digit' })
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { isUpdatingDonation } = useDonation()
  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const selectedPaymentOption = useSelectedPaymentOption()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const hasDownPayment = parseFloat(selectedPaymentOption?.amountDueToday.amount ?? '') > 0
  const paymentsCount = selectedPaymentOption?.installmentsCount ?? 0

  const useRenderPaymentScheduleItem = (paymentSchedule: CheckoutPaymentSchedule, index: number): JSX.Element => {
    const isLoading = isShippingOptionUpdating || (index === 0 && isUpdatingDonation)
    const isDownPayment = hasDownPayment && index === 0
    const sequence = hasDownPayment ? index : index + 1
    const sequenceText = isDownPayment
      ? t('consumerLending:paymentSchedule:downPayment')
      : sequence === paymentsCount
      ? t('consumerLending:paymentSchedule:finalPayment')
      : t('consumerLending:paymentSchedule:paymentSequence', { sequence, paymentsCount })

    const dateText = isDownPayment ? t('consumerLending:paymentSchedule:today') : formatDate(paymentSchedule.installmentDueDate)

    return (
      <Grid center key={`paymentScheduleItem-${index}`}>
        <div>
          <Text size={textSize} bold data-testid={`paymentScheduleItem-date-${index}`}>
            {dateText}
          </Text>
          <Text color='Gray40' size={smallTextSize} data-testid={`paymentScheduleItem-sequence-${index}`}>
            {sequenceText}
          </Text>
        </div>
        <Grid.FixedItem width={108} align='right' data-testid={`paymentScheduleItem-money-${index}`}>
          <Money value={paymentSchedule.amount} size={textSize} loading={isLoading} />
        </Grid.FixedItem>
      </Grid>
    )
  }

  return (
    <div className={designLanguageClass.wrapper}>
      <Accordion
        id='consumerLending-payment-schedule'
        onChange={(isOpen) => {
          if (isOpen) {
            logEvent(TrackingEvent.TOGGLED_CL_PAYMENT_SCHEDULE)
          }
        }}
      >
        <Accordion.Button>
          <Grid center>
            <Text size={textSize}>{t('common:paymentSchedule')}</Text>
            <Grid.FixedItem width={108} align='right'>
              <Text size={textSize}>{t('consumerLending:paymentsCount', { count: paymentsCount, context: selectedPaymentOption?.paymentInterval })}</Text>
            </Grid.FixedItem>
          </Grid>
        </Accordion.Button>
        <Accordion.Details>
          <div className={designLanguageClass.paymentSchedulesContainer}>{paymentSchedules.map(useRenderPaymentScheduleItem)}</div>
        </Accordion.Details>
      </Accordion>
    </div>
  )
}
